import { HorizontalNavigationTab } from "@risalabs_frontend_org/oasis-ui-kit";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { LoaderMessage } from "../../components/loaderMessage/loaderMessage";
import { CredentialDetailsModal } from "../../components/modals/credentialDetailsModal/credentialDetailsModal";
import { CredentialDetailsPanelContainer } from "./components/CredentialDetailsPanelContainer";
import { CredentialsConfigHeader } from "./components/CredentialsConfigHeader";
import { CredentialsPortalAccordionList } from "./components/CredentialsPortalAccordionList";
import { useCredentialsData } from "./hooks/useCredentialsData";
import { useCredentialSelection } from "./hooks/useCredentialSelection";
import { useFetchHealthFacility } from "./hooks/useFetchHealthFacility";

const BASE_CLASS_NAME_FOR_CREDENTIALS_CONFIG = "credentials-configuration";

/**
 * Credentials configuration page with a two-panel layout.
 *
 * Left panel: portal-grouped credential accordion list (independently scrollable).
 * Right panel: selected credential detail view (independently scrollable).
 *
 * Tabs filter credentials by portal type (MFA, OTP, Non-Queue, etc.).
 */
const CredentialsConfig = () => {
  const { id } = useParams();

  const [selectedTab, setSelectedTab] = useState<string>("MFA");
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [isCredentialDetailsModalOpen, setIsCredentialDetailsModalOpen] =
    useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const {
    credentials,
    isLoading,
    clientName,
    isLoadingClient,
    clientOptions,
    groupedCredentials,
    groupedCredentialsByPortal,
  } = useCredentialsData(selectedClientId);

  const {
    selectedCredentialId,
    credentialDetails,
    detailsCardData,
    selectCredential,
    resetSelection,
  } = useCredentialSelection({
    credentials,
    selectedClientId,
    urlId: id,
  });

  useFetchHealthFacility();

  const activeTabGroup = groupedCredentialsByPortal.find(
    (group) => group.value === selectedTab,
  );
  const activeTabCredentials = activeTabGroup?.credentials ?? [];

  const handleAddClick = () => {
    setIsEditMode(false);
    setIsCredentialDetailsModalOpen(true);
  };

  const handleEditClick = () => {
    setIsEditMode(true);
    setIsCredentialDetailsModalOpen(true);
  };

  /**
   * Handles client dropdown selection and resets credential selection.
   *
   * @param option - Selected dropdown option containing client value
   */
  const handleClientChange = (option: any) => {
    setSelectedClientId(option.value);
    resetSelection();
  };

  return (
    <div
      className={`${BASE_CLASS_NAME_FOR_CREDENTIALS_CONFIG}-page-layout flex h-full w-full flex-col overflow-hidden p-2`}
    >
      <div
        className={`${BASE_CLASS_NAME_FOR_CREDENTIALS_CONFIG}-content-container flex h-full flex-col overflow-hidden`}
      >
        <CredentialsConfigHeader
          baseClassName={BASE_CLASS_NAME_FOR_CREDENTIALS_CONFIG}
          isLoadingClient={isLoadingClient}
          clientOptions={clientOptions}
          onClientChange={handleClientChange}
          onAddCredentialClick={handleAddClick}
          showAddCredential={!!selectedClientId}
        />
        {isLoading ? (
          <div className="h-full flex-1 items-center justify-center">
            <LoaderMessage message="Loading form configurations..." />
          </div>
        ) : (
          <div className="flex w-full flex-1 flex-col gap-3 overflow-hidden">
            <HorizontalNavigationTab
              tabs={groupedCredentials.map((group) => ({
                label: group.label,
                value: group.value,
              }))}
              onChange={(value) => {
                setSelectedTab(value);
              }}
              value={selectedTab}
              backgroundColor="white"
              className="border-b border-primaryGray-14"
            />
            {!selectedClientId ? (
              <div className="flex flex-1 items-center justify-center text-gray-500">
                Select a client to view credentials
              </div>
            ) : activeTabCredentials.length === 0 ? (
              <div className="flex flex-1 items-center justify-center text-gray-500">
                No credentials found
              </div>
            ) : (
              <div className="flex flex-1 gap-3 overflow-hidden">
                <div className="flex-1 overflow-y-auto">
                  <CredentialsPortalAccordionList
                    portalGroups={activeTabGroup?.portalGroups ?? []}
                    selectedCredentialId={selectedCredentialId}
                    onSelectCredential={selectCredential}
                  />
                </div>
                <div className="flex-1 overflow-y-auto">
                  <CredentialDetailsPanelContainer
                    credentialDetails={credentialDetails}
                    detailsCardData={detailsCardData}
                    selectedCredentialId={selectedCredentialId}
                    onEditClick={handleEditClick}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {isCredentialDetailsModalOpen && (
          <CredentialDetailsModal
            isOpen={isCredentialDetailsModalOpen}
            onClose={() => setIsCredentialDetailsModalOpen(false)}
            isEditMode={isEditMode}
            credentialData={credentialDetails}
            clientId={selectedClientId?.toString() ?? ""}
            client_Name={
              selectedClientId
                ? clientName.find((client) => client.DocId === selectedClientId)
                    ?.name
                : ""
            }
          />
        )}
      </div>
    </div>
  );
};

export default CredentialsConfig;
